import { useEffect, useState } from 'react';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Typography, Stack, Box, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { globalGetService, globalPostService, globalPutService } from '../../../utils/globalApiServices';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { checkApiStatus, numberWithCommas } from '../../../utils';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import NumericLabel from 'react-pretty-numbers';
import { changeInvoiceStatusApi, getInvoiceApi, getInvoiceConstant, getOffsetDetailApi, offsetDropdownApi } from '../apiService';
import { jetStreamInstance, PageLoader, titanAviationIntance, gaTelesisInstance } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';
import { regexConstants } from '../../../constants/regEx';
import { getSignedTaxAmount, getSignedTaxPercentage } from '..';

const ChangeInvoiceStatus = ({ onToggleModal, invoiceStatus, getInvoice=()=>{}, getResponseBack, invoiceType, offsetInvoice, getPartialPaymentList=()=>{}, getOffsetList=()=>{}, getLinkedInvoices=()=>{} }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [invoice, setInvoice] = useState({ ...invoiceStatus?.data, payment_date: null, sent_date: moment().format(backendDateFormat), last_payment_date: invoiceStatus?.data?.payment_date } || {})
    const [tableErrors, setTableErrors] = useState({});
    const [offsetTableErrors, setOffsetTableErrors] = useState({});
    const [error, setError] = useState({});
    const [invoiceConstants, setInvoiceConstants] = useState([]);
    const Amount = invoice?.invoice_items?.reduce((total, next) => total + Math.floor((parseFloat(next.paid_amount) || 0) * 100), 0) / 100;
    const RemainingAmount = invoice?.invoice_items?.reduce((total, next) => total + Math.floor((parseFloat(next.remaining_amount) || 0) * 100), 0) / 100;
    const [offsetDropdown, setOffsetDropdown] = useState([]);
    const [offsetDropdownDetail, setOffsetDropdownDetail] = useState([]);
    const [selectedOffset, setSelectedOffset] = useState(null);
    const [dropDownloader, setDropDownLoader] = useState({ offsetDropdownList: false, })
    const [detailFetched, setDetailFetched] = useState(false);
    const [isFetchingPartialItems, setIsFetchingPartialItems] = useState(false);
    const [isFullCnOffset, setIsFullCnOffset] = useState(false);

    const showCnOffsetRadios = [3].includes(invoice?.status?.value) && (titanAviationIntance || gaTelesisInstance);

    useEffect(() => {
        getInvoiceConstants()
        if (["Rental", "Reserve"].includes(invoiceType)) {
            getOffsetDropdown()
        }
    }, [])

    useEffect(() => {
        if (!showCnOffsetRadios && isFullCnOffset) {
            setIsFullCnOffset(false);
        }
    }, [showCnOffsetRadios, isFullCnOffset]);
    
    useEffect(() => {
        if (offsetInvoice?.[0] && offsetDropdown?.length) {
            const match = offsetDropdown.find(opt => opt.slug === offsetInvoice[0]);
            if (match) {
                setSelectedOffset(match);
                getOffsetDropdownDetail(match.slug);
            }
        }
    }, [offsetInvoice, offsetDropdown]);

    const onFieldChange = (keyParam, value, invoiceItem, itemIndex) => {
        if (invoiceItem) {
            setInvoice({
                ...invoice,
                invoice_items: invoice.invoice_items.map((item, index) => index !== itemIndex ? item : {
                    ...item, [keyParam]: value
                })
            })
        }
        else {
            setInvoice({ ...invoice, [keyParam]: value })
        }
    }

    const onOffsetItemChange = (keyParam, value, itemIndex, extraUpdates = {}) => {
        setOffsetDropdownDetail({
            ...offsetDropdownDetail,
            invoice_items: offsetDropdownDetail.invoice_items.map((item, index) =>
                index !== itemIndex ? item : { ...item, [keyParam]: value, ...extraUpdates }
            )
        });
    }

    const hasUnmatchedOffsetAssets = () => offsetMatchedRows.some(row => !row.leftItem && row.rightItem);

    const applyFullCnOffsetAmounts = () => {
        setOffsetDropdownDetail(prev => ({
            ...prev,
            invoice_items: (prev?.invoice_items || []).map((item, index) =>
                matchedOffsetIndexes.has(index) ? {
                    ...item,
                    offset_amount: item?.remaining_amount ?? 0,
                    paid_amount: 0
                } : item
            )
        }));
    };

    const handleCnOffsetModeChange = (e) => {
        const wantsFullCnOffset = e.target.value === 'apply_full_cn_offset';
        if (!wantsFullCnOffset) {
            setIsFullCnOffset(false);
            return;
        }
        if (hasUnmatchedOffsetAssets()) {
            enqueueSnackbar('Full CN Offset cannot be applied: some items are missing in the Parent Invoice Detail.', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            setIsFullCnOffset(true);
            return;
        }
        setIsFullCnOffset(true);
        applyFullCnOffsetAmounts();
    };

    const onRestErrorKey = (keyParam) => {
        setError({ ...error, [keyParam]: '' })
    }
    
    const getInvoiceConstants = () => {
        setLoading(true);
        globalPostService(getInvoiceConstant(), { 'constant_types': ["invoice_status"] })
            .then(response => {
                if (checkApiStatus(response)) {
                    setLoading(false);
                    setInvoiceConstants(response?.data?.data);
                }
            })
    }

    const getOffsetDropdown = () => {
        setLoading(true);
        setDropDownLoader(prev => ({ ...prev, offsetDropdownList: true }))
        globalGetService(offsetDropdownApi(invoice?.slug))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, offsetDropdownList: false }))
                if (checkApiStatus(response)) {
                    setLoading(false);
                    setOffsetDropdown(response?.data?.data);
                    setDropDownLoader(false)
                }
            })
    }

    const getOffsetDropdownDetail = (slug) => {
        if (!slug) return;

        setLoading(true);
        globalGetService(getOffsetDetailApi(slug))
            .then(response => {
                if (checkApiStatus(response)) {
                    setOffsetDropdownDetail(response?.data?.data);
                }
            })
            .finally(() => setLoading(false));
    };

    const fetchInvoiceItemsForPartialPaid = (slug) => {
        if (!slug) {
            return;
        }
        if (detailFetched) {
            return;
        }
        setIsFetchingPartialItems(true);
        globalGetService(getInvoiceApi({ slug }))
            .then(response => {
                if (checkApiStatus(response)) {
                    const fetchedItems = response?.data?.data?.invoice_items || [];
                    setInvoice(prev => ({
                        ...prev,
                        balance_amount: response?.data?.data?.balance_amount ?? prev.balance_amount,
                        invoice_items: fetchedItems.map(item => ({
                            ...item,
                            paid_amount: 0
                        }))
                    }));
                    setDetailFetched(true);
                } else {
                    enqueueSnackbar('Failed to load invoice items. Please try again.', {
                        variant: 'error',
                        anchorOrigin: { horizontal: 'right', vertical: 'top' }
                    });
                }
            })
            .finally(() => setIsFetchingPartialItems(false))
    };

    useEffect(() => {
        if ([2, 3, 5, 6].includes(invoiceStatus?.data?.status?.value)) {
            fetchInvoiceItemsForPartialPaid(invoiceStatus?.data?.slug);
        }
    }, []);

    const linkedStatus = invoiceStatus?.data?.status
    const allInvStauts = jetStreamInstance ?
    invoiceConstants?.filter(status => linkedStatus?.value === 1 ? [2, 4, 6].includes(status?.value) : linkedStatus?.value === 2 ? [3, 4, 5, 6].includes(status?.value) : linkedStatus?.value === 5 ? [3, 4, 5, 6].includes(status?.value) : linkedStatus?.value === 6 ? [3, 5].includes(status?.value) :true):
    invoiceConstants?.filter(status => linkedStatus?.value === 1 ? [2, 4].includes(status?.value) : linkedStatus?.value === 2 ? [3, 4, 5].includes(status?.value) : linkedStatus?.value === 5 ? [3, 4, 5].includes(status?.value) : true)
    const filteredInvoiceStatus =  allInvStauts
       ?.filter(status => !(invoice?.amount === 0 && status?.value === 5))
        ?.sort((a, b) => (a?.value === 4 ? 1 : 0) - (b?.value === 4 ? 1 : 0));
    
    const onChangeInvoiceStatus = () => {

        let validationInputs = {};
        let newErrors = {};
        let newOffsetErrors = {};
        const statusValue = invoice?.status?.value;

        if (statusValue === 3 || statusValue === 5) {
            const pd = invoice?.payment_date;
            let paymentDateError = "";

            if (!pd) {
                paymentDateError = "Payment Date is required.";
            } else {
                const m = moment(pd, backendDateFormat, true);

                if (!m.isValid()) {
                    paymentDateError = "Please enter a valid date.";
                } else {
                    if (m.isAfter(moment())) {
                        paymentDateError = "Payment Date cannot be in the future.";
                    }
                    else if (invoiceStatus?.data?.sent_date && m.isBefore(moment(invoiceStatus?.data?.sent_date))) {
                        paymentDateError = `Payment Date must be on or after the Sent Date (${moment(invoiceStatus?.data?.sent_date).format(fieldDateFormat)}).`;
                    }
                    else if (m.isBefore(moment(invoice?.last_payment_date))) {
                        paymentDateError = `Payment date cannot be before (${moment(invoice.last_payment_date).format(fieldDateFormat)}).`;
                    }
                }
            }
            validationInputs = {
                ...validationInputs,
                payment_date: paymentDateError
            };
        }

        if (statusValue === 2) {
            const sd = invoice?.sent_date;
            let sentDateError = "";

            if (!sd) {
                sentDateError = "Sent Date is required.";
            } else {
                const m = moment(sd, backendDateFormat, true);
                if (!m.isValid()) {
                    sentDateError = "Please enter a valid date.";
                } else if (m.isAfter(moment())) {
                    sentDateError = "Sent Date cannot be in the future.";
                } else if (invoice?.date && m.isBefore(moment(invoice?.date))) {
                    sentDateError = `Sent Date must be on or after the Invoice Date (${moment(invoice.date).format(fieldDateFormat)}).`;
                }
            }
            validationInputs = {
                ...validationInputs,
                sent_date: sentDateError
            };
        }

        if (statusValue === 4) {
            validationInputs = { reason: invoice?.reason ? "" : "Please enter Reason", };
        }

        if (statusValue === 5) {
            const hasOffsetItems = offsetDropdownDetail?.invoice_items?.length > 0;

            if (!hasOffsetItems) {
                // Left table only shown when no offset items — validate invoice_items paid_amount
                invoice?.invoice_items?.forEach((row, index) => {
                    const errors = {};
                    const remaining = parseFloat(row.remaining_amount || 0);
                    const paid = parseFloat(row.paid_amount || 0);
                    if (remaining > 0) {
                        if (isNaN(paid) || String(row.paid_amount ?? '').trim() === ".") {
                            errors.paid_amount = "Please enter a valid Amount";
                        }
                    }
                    if (Object.keys(errors).length > 0) {
                        newErrors[index] = errors;
                    }
                });
                setTableErrors(newErrors);

                const hasNegativeTaxOnTotal = invoice?.tax_details?.some(tax => tax?.is_applied_on_total === true && tax?.type?.value === 2);
                const activeItems = (invoice?.invoice_items || []).filter(item => parseFloat(item.remaining_amount || 0) > 0);
                const allPaidZero = activeItems.length > 0 && activeItems.every(item => !parseFloat(item.paid_amount || 0));
                if (allPaidZero) validationInputs.all_paid_zero = 'error';

                const totalRemaining = invoice?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.remaining_amount) || 0), 0);
                const totalPaid = invoice?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.paid_amount) || 0), 0);
                if (totalPaid > 0 && totalRemaining > 0 && totalPaid === totalRemaining && !hasNegativeTaxOnTotal) {
                    validationInputs.full_paid_on_partial = 'error';
                }

                if (hasNegativeTaxOnTotal) {
                    const totalAmount = parseFloat(invoice?.balance_amount) || 0;
                    if (totalPaid >= totalAmount) {
                        validationInputs.full_paid_on_partial_in_negative_tax = 'error';
                    }
                }
            }

            if (hasOffsetItems) {
                const anyOffsetEntered = offsetDropdownDetail?.invoice_items?.some((row, i) => {
                    const leftRemaining = parseFloat(invoice?.invoice_items?.[i]?.remaining_amount || 0);
                    if (leftRemaining === 0) return false;
                    const offset = parseFloat(row?.offset_amount || 0);
                    return offset > 0;
                });

                offsetDropdownDetail?.invoice_items?.forEach((row, index) => {
                    const leftRemaining = parseFloat(invoice?.invoice_items?.[index]?.remaining_amount || 0);
                    if (leftRemaining === 0) return; // field is disabled — skip validation

                    const errors = {};
                    const remaining = parseFloat(row.remaining_amount || 0);
                    const offsetValue = String(row?.offset_amount ?? "");
                    const offset = parseFloat(offsetValue || 0);

                    if (offsetValue && offsetValue.trim() !== "" && (isNaN(offset) || offsetValue.trim() === ".")) {
                        errors.offset_amount = "Please enter a valid Offset Amount";
                    }

                    const paidValue = String(row?.paid_amount ?? "");
                    const paid = parseFloat(paidValue || 0);
                    if (isNaN(paid) || paidValue.trim() === ".") {
                        errors.paid_amount = "Please enter a valid Amount";
                    }

                    if (Object.keys(errors).length > 0) {
                        newOffsetErrors[index] = errors;
                    }
                });
                setOffsetTableErrors(newOffsetErrors);

                const hasNegativeTaxOnTotal = invoice?.tax_details?.some(tax => tax?.is_applied_on_total === true && tax?.type?.value === 2);
                const activeOffsetItems = (offsetDropdownDetail?.invoice_items || []).filter((_, i) => parseFloat(invoice?.invoice_items?.[i]?.remaining_amount || 0) > 0);
                const allOffsetPaidZero = activeOffsetItems.length > 0 && activeOffsetItems.every(item => !parseFloat(item.paid_amount || 0));
                if (allOffsetPaidZero && !anyOffsetEntered) validationInputs.all_paid_zero = 'error';
                if (!anyOffsetEntered) validationInputs.all_offset_zero = 'error';

                const totalBalance = invoice?.invoice_items?.reduce((sum, item, i) => {
                    const leftRemaining = parseFloat(item?.remaining_amount || 0);
                    const offsetAmt = parseFloat(offsetDropdownDetail?.invoice_items?.[i]?.offset_amount || 0);
                    return sum + Math.max(leftRemaining - offsetAmt, 0);
                }, 0);
                const totalPaid = offsetDropdownDetail?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.paid_amount) || 0), 0);
                if (totalPaid > 0 && totalBalance > 0 && totalPaid === totalBalance && !hasNegativeTaxOnTotal) {
                    validationInputs.full_paid_on_partial = 'error';
                }

                if (hasNegativeTaxOnTotal) {
                    const totalAmount = parseFloat(invoice?.balance_amount) || 0;
                    if (totalPaid >= totalAmount) {
                        validationInputs.paid_amount_exceeds_invoice_balance = 'error';
                    }
                }

                const hasNegativeTaxOnTotalOffset = offsetDropdownDetail?.tax_details?.some(tax => tax?.is_applied_on_total === true && tax?.type?.value === 2);
                if (hasNegativeTaxOnTotalOffset) {
                    const totalOffsetAmount = offsetDropdownDetail?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.offset_amount) || 0), 0) || 0;
                    const offsetBalanceAmount = parseFloat(offsetDropdownDetail?.balance_amount) || 0;
                    if (totalOffsetAmount > offsetBalanceAmount) {
                        validationInputs.offset_amount_exceeds_offset_balance = 'error';
                    }
                }
            }
        }

        if (statusValue === 3) {
            const anyOffsetEntered3 = offsetDropdownDetail?.invoice_items?.some((row, i) => {
                const leftRemaining = parseFloat(invoice?.invoice_items?.[i]?.remaining_amount || 0);
                if (leftRemaining === 0) return false;
                const offset = parseFloat(row?.offset_amount || 0);
                return offset > 0;
            });

            offsetDropdownDetail?.invoice_items?.forEach((row, index) => {
                const leftRemaining = parseFloat(invoice?.invoice_items?.[index]?.remaining_amount || 0);
                if (leftRemaining === 0) return;

                const errors = {};
                const offsetValue = String(row?.offset_amount ?? "");
                const offset = parseFloat(offsetValue || 0);

                if (offsetValue && offsetValue.trim() !== "" && (isNaN(offset) || offsetValue.trim() === ".")) {
                    errors.offset_amount = "Please enter a valid Offset Amount";
                }

                if (Object.keys(errors).length > 0) {
                    newOffsetErrors[index] = errors;
                }
            });
            setOffsetTableErrors(newOffsetErrors);

            const hasNegativeTaxOnTotalOffset = offsetDropdownDetail?.tax_details?.some(tax => tax?.is_applied_on_total === true && tax?.type?.value === 2);
            if (!anyOffsetEntered3 && ["Rental", "Reserve"].includes(invoiceType) && offsetDropdownDetail?.invoice_items?.length > 0) validationInputs.all_offset_zero = 'error';

            if (hasNegativeTaxOnTotalOffset) {
                const totalOffsetPaid = offsetDropdownDetail?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.offset_amount) || 0), 0) || 0;
                const totalAmount = parseFloat(offsetDropdownDetail?.balance_amount) || 0;
                if (totalOffsetPaid > totalAmount) {
                    validationInputs.offset_amount_exceeds_offset_balance = 'error';
                }
            }
        }

        if ((statusValue === 3 || statusValue === 5) && isFullCnOffset && hasUnmatchedOffsetAssets()) {
            validationInputs.full_cn_offset_asset_mismatch = 'error';
        }

        // Prepare payload
        let payload = {
            slug: invoice?.slug || null,
            status: invoice?.status || null,
        };

        if (statusValue === 5) {
            payload.payment_date = invoice?.payment_date || null;
            payload.apply_manually_cn_offset = !isFullCnOffset;
            payload.apply_full_cn_offset = isFullCnOffset;
            payload.invoice_items = (invoice?.invoice_items || []).map((item) => {
                const remaining = parseFloat(item.remaining_amount || 0);

                return {
                    ...item,
                    paid_amount: remaining === 0 ? 0 : parseFloat(item.paid_amount) || 0
                };
            });
        }

        if (statusValue === 5 && offsetDropdownDetail?.invoice_items?.length > 0 ) {
            payload.payment_date = invoice?.payment_date || null;
            payload.invoice_items = (invoice?.invoice_items || []).map((item, index) => {
                const remaining = parseFloat(item.remaining_amount || 0);
                const matchedOffsetItem = offsetMatchedRows[index]?.rightItem;
                const paidSource = matchedOffsetItem ? matchedOffsetItem?.paid_amount : item?.paid_amount;

                return {
                    ...item,
                    paid_amount: remaining === 0 ? 0 : parseFloat(paidSource || 0),
                };
            });
            payload.offset_invoice = Array.isArray(invoice?.offset_invoice_slugs)
                ? invoice.offset_invoice_slugs
                : invoice.offset_invoice;
            payload.offset_invoice_items = (offsetDropdownDetail?.invoice_items || []).map((item, index) => {
                const leftRemaining = parseFloat(invoice?.invoice_items?.[index]?.remaining_amount || 0);
                const balance = offsetBalanceByIndex[index];
                const offsetItemPayload = {
                    ...item,
                    offset_amount: leftRemaining === 0 ? 0 : parseFloat(item?.offset_amount || 0),
                    paid_amount: leftRemaining === 0 ? 0 : parseFloat(item?.offset_amount || 0),
                    offset_excess_amount: balance < 0 ? balance : 0,
                };
                return offsetItemPayload;
            });
        }

        if (statusValue === 4) {
            payload.reason = invoice?.reason || null;
        }

        if (invoice?.status?.value === 2) {
            payload.sent_date = invoice?.sent_date || null;
            payload.offset_invoice = Array.isArray(invoice?.offset_invoice_slugs)
                ? invoice.offset_invoice_slugs
                : [];
        }

        if (statusValue === 3) {
            payload.payment_date = invoice?.payment_date || null;
            payload.apply_manually_cn_offset = !isFullCnOffset;
            payload.apply_full_cn_offset = isFullCnOffset;
            payload.offset_invoice = Array.isArray(invoice?.offset_invoice_slugs)
                ? invoice.offset_invoice_slugs
                : invoice.offset_invoice;
            payload.offset_invoice_items = (offsetDropdownDetail?.invoice_items || []).map((item, index) => {
                const balance = offsetBalanceByIndex[index];
                const offsetItemPayload = {
                    ...item,
                    paid_amount: Number(offsetDropdownDetail?.invoice_items?.[index]?.offset_amount || 0),
                    offset_excess_amount: balance < 0 ? balance : 0,
                };
                return offsetItemPayload;
            });

            const excessAmountRows = Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0
                ? offsetMatchedRows.map((row) => ({
                    item_id: row.leftItem?.id,
                    amount: parseFloat((row.rightItem ? row.rightItem?.amount : row.leftItem?.amount) || 0) || 0
                }))
                : (invoice?.invoice_items || []).map((item) => ({
                    item_id: item?.id,
                    amount: parseFloat(item?.amount) || 0
                }));
            const excessAmount = excessAmountRows.filter((row) => row.item_id != null && row.amount > 0);
            if (excessAmount.length > 0) {
                payload.excess_amount = excessAmount;
            }
        }

        const hasOffsetItems = Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0;
        const isValid =
            (statusValue !== 2 && statusValue !== 3 && statusValue !== 5 && statusValue !== 4) || 
            (
                Object.keys(validationInputs).every((k) => validationInputs[k] === "") &&
                Object.keys(newErrors).length === 0 &&
                (!hasOffsetItems || Object.keys(newOffsetErrors).length === 0)
            );

        if (isValid) {
            setLoading(true);
            globalPutService(changeInvoiceStatusApi(), payload).then((response) => {
                if (checkApiStatus(response)) {
                    onToggleModal();
                    setLoading(false);
                    getInvoice();
                    getResponseBack();
                    getPartialPaymentList();
                    getOffsetList();
                    getLinkedInvoices();
                    enqueueSnackbar(response?.data?.message, {
                        variant: "success",
                        anchorOrigin: { horizontal: "right", vertical: "top" },
                    });
                    trackActivity('Item Edited', { page_title: `Changes Invoice Status`, success_msg: response?.data?.message, payload, response: response?.data })
                } else {
                    setLoading(false);
                    enqueueSnackbar(response?.data?.message, {
                        variant: "error",
                        anchorOrigin: { horizontal: "right", vertical: "top" },
                    });
                    trackActivity('Item Edited', { page_title: `Changes Invoice Status`, error_msg: response?.data?.message, payload, response: response?.data })
                }
            });
        } else {
            if (statusValue === 2 || statusValue === 3 || statusValue === 5 || statusValue === 4) {
                setError(validationInputs);
                if (validationInputs.all_paid_zero && validationInputs.all_offset_zero) {
                    enqueueSnackbar("Please enter an Offset Amount greater than 0 for at least one Invoice Item(s)", { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                } else if (validationInputs.all_paid_zero) {
                    enqueueSnackbar("Please enter an Paid Amount greater than 0 for at least one Invoice Item(s)", { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                } else if (validationInputs.all_offset_zero) {
                    enqueueSnackbar("Please enter an Offset Amount greater than 0 for at least one Invoice Item(s)", { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
                if (validationInputs.full_paid_on_partial) {
                    enqueueSnackbar("If you partially paid, you can't enter the full paid amount", { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
                if (validationInputs.full_paid_on_partial_in_negative_tax) {
                    enqueueSnackbar(`For a partial payment, the total paid amount cannot be equal to or greater than the total amount ${invoice?.balance_amount}`, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
                if (validationInputs.offset_amount_exceeds_offset_balance) {
                    enqueueSnackbar(`The offset amount cannot be greater than the Remaining amount ${offsetDropdownDetail?.balance_amount}`, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
                if (validationInputs.paid_amount_exceeds_invoice_balance) {
                    enqueueSnackbar(`The paid amount cannot be equal to or greater than the balance amount ${invoice?.balance_amount}`, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
                if (validationInputs.full_cn_offset_asset_mismatch) {
                    enqueueSnackbar('Full CN Offset cannot be applied: some items are missing in the Parent Invoice Detail.', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
                }
            }
        }
    };

    const resetTableError = (rowIndex, fieldName) => {
        setTableErrors((prevErrors) => {
            const rowErrors = { ...prevErrors[rowIndex] };
            delete rowErrors[fieldName];
            const updatedErrors = { ...prevErrors };
            if (Object.keys(rowErrors).length === 0) {
                delete updatedErrors[rowIndex];
            } else {
                updatedErrors[rowIndex] = rowErrors;
            }
            return updatedErrors;
        });
    };

    const resetOffsetTableError = (rowIndex, fieldName) => {
        setOffsetTableErrors((prevErrors) => {
            const rowErrors = { ...prevErrors[rowIndex] };
            delete rowErrors[fieldName];
            const updatedErrors = { ...prevErrors };
            if (Object.keys(rowErrors).length === 0) {
                delete updatedErrors[rowIndex];
            } else {
                updatedErrors[rowIndex] = rowErrors;
            }
            return updatedErrors;
        });
    };

    const handleStatusChange = (newValue) => {
        const updatedInvoiceItems = (invoice?.invoice_items || []).map(item => ({
            ...item,
            paid_amount: newValue?.value === 5 ? 0 : ''
        }));

        setInvoice({
            ...invoice,
            status: newValue,
            payment_date: null,
            invoice_items: updatedInvoiceItems
        });

        setError({});
        setTableErrors({});
        setOffsetTableErrors({});

        if (newValue?.value === 5) {
            fetchInvoiceItemsForPartialPaid(invoice?.slug);
        }
    }

    const isDataChanged = () => {
        const originalStatus = invoiceStatus?.data?.status?.value;
        const currentStatus = invoice?.status?.value;

        const hasPaidAmount = invoice?.invoice_items?.some(
            item => item?.paid_amount
        );

        if (currentStatus === 5) {
            if (offsetDropdownDetail?.invoice_items?.length > 0) {
                return offsetDropdownDetail.invoice_items.some(item => item?.offset_amount);
            }
            return hasPaidAmount;
        }
        if (currentStatus !== originalStatus) return true;
        if (currentStatus === 2 && invoice?.sent_date !== invoiceStatus?.data?.sent_date) return true;
        return false;
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Change Invoice Status`, page_type: page_type, })
    }

    // Match Offset Invoice Detail rows to Parent Invoice Detail rows by (asset_id, invoice_item_name)
    // instead of array index, so the two tables stay aligned regardless of invoice_items order.
    const parentInvoiceItems = invoice?.invoice_items || [];
    const offsetInvoiceItems = offsetDropdownDetail?.invoice_items || [];
    const getOffsetItemKey = (item) => `${item?.asset_id ?? item?.asset?.id ?? ''}__${item?.invoice_item_name ?? ''}`;
    const offsetItemIndexByKey = new Map();
    offsetInvoiceItems.forEach((item, idx) => {
        offsetItemIndexByKey.set(getOffsetItemKey(item), idx);
    });
    const matchedOffsetIndexes = new Set();
    const offsetMatchedRows = parentInvoiceItems.map((leftItem) => {
        const rightIndex = offsetItemIndexByKey.get(getOffsetItemKey(leftItem));
        if (rightIndex !== undefined) matchedOffsetIndexes.add(rightIndex);
        return {
            leftItem,
            rightItem: rightIndex !== undefined ? offsetInvoiceItems[rightIndex] : undefined,
            rightIndex
        };
    });
    offsetInvoiceItems.forEach((item, idx) => {
        if (!matchedOffsetIndexes.has(idx)) {
            offsetMatchedRows.push({ leftItem: undefined, rightItem: item, rightIndex: idx });
        }
    });

    const offsetBalanceByIndex = {};
    offsetMatchedRows.forEach(({ leftItem, rightItem, rightIndex }) => {
        if (leftItem && rightIndex !== undefined) {
            offsetBalanceByIndex[rightIndex] = (parseFloat(leftItem?.remaining_amount) || 0) - (parseFloat(rightItem?.offset_amount) || 0);
        }
    });

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth={Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0 ? 'xl' : 'md'}
                open={invoiceStatus.modal}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header' id="customized-dialog-title">
                    Change Invoice Status
                </DialogTitle>
                <DialogContent dividers style={{ minWidth: "400px", maxWidth: Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0 ? "none" : "800px", minHeight: "140px", padding: "16px" }}>
                    <Grid container spacing={3}>
                        <Grid size={{
                            md: (() => {
                                const status = invoice?.status?.value;
                                if (status === 4) return 12;
                                if (offsetDropdownDetail?.invoice_items?.length > 0) return 4;
                                if (status === 2 && offsetDropdown?.length > 0 && ["Rental", "Reserve"].includes(invoiceType)) return 6;
                                if (status === 2) { return 12; }
                                if (status === 3) { return ["Rental", "Reserve"].includes(invoiceType) ? 6 : 12; }
                                if ([5].includes(status)) { return 6; } return 12;
                            })(), xs: 12
                        }} >
                            <Autocomplete
                                disablePortal
                                options={filteredInvoiceStatus}
                                getOptionLabel={(option) => option.label || ""}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={invoice?.status}
                                onFocus={() => onRestErrorKey("status")}
                                disableClearable
                                onChange={(e, newValue) => handleStatusChange(newValue)}
                                onInputChange={(event, newInputValue) => {
                                    if (newInputValue.trim() !== "") {
                                        setError((prev) => ({ ...prev, currency: "" }));
                                    }
                                }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            maxHeight: [5, 4].includes(invoice?.status?.value) || [3].includes(invoice?.status?.value) && ["Rental", "Reserve"].includes(invoiceType) ? 130 : 135,
                                            overflowY: 'auto',
                                            "& .MuiAutocomplete-option": {
                                                minHeight: 26,
                                                py: '4px',
                                            },
                                        },
                                    },
                                }}
                                renderInput={(params) => (<TextField required {...params} label="Status" margin='normal' size='small' helperText={error.status} slotProps={{ inputLabel: { shrink: true } }} />)}
                            />
                        </Grid>
                        {invoice?.status?.value === 2 ? <Grid size={{ md: offsetDropdownDetail?.invoice_items?.length > 0 ? 4 : offsetDropdown?.length > 0 && ["Rental", "Reserve"].includes(invoiceType) ? 6 : 12, xs: 12 }} >
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Sent Date"
                                    value={invoice?.sent_date ? moment(invoice.sent_date) : null}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                                        onFieldChange("sent_date", formattedDate);
                                        if (isValid) {
                                            if (moment(data).isAfter(moment(), 'day')) {
                                                setError((prev) => ({ ...prev, sent_date: "Sent Date cannot be in the future." }));
                                            } else {
                                                onRestErrorKey("sent_date");
                                            }
                                        }
                                    }}
                                    disableFuture
                                    minDate={moment(invoice?.date)}
                                    slotProps={{
                                        textField: {
                                            required: "true",
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.sent_date || false,
                                            helperText: error.sent_date || '',
                                            onFocus: () => {
                                                if (error.sent_date) {
                                                    onRestErrorKey("sent_date");
                                                }
                                            }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("sent_date", null);
                                                onRestErrorKey("sent_date");
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid> : null}
                        {[3, 5].includes(invoice?.status?.value) ?
                            <Grid size={{ md: offsetDropdownDetail?.invoice_items?.length > 0 ? 4 : invoice?.status?.value === 5 ? 6 : invoice?.status?.value === 3 && ["Rental", "Reserve"].includes(invoiceType) ? 6 : 12, xs: 12 }} >
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Payment Date"
                                        value={invoice?.payment_date ? moment(invoice.payment_date) : null}
                                        onChange={(data) => {
                                            const isValid = moment(data)?.isValid();
                                            const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                                            onFieldChange("payment_date", formattedDate);
                                            if (isValid) {
                                                onRestErrorKey("payment_date");
                                            }
                                        }}
                                        disableFuture
                                        minDate={moment(invoiceStatus?.data?.sent_date)}
                                        slotProps={{
                                            textField: {
                                                required: "true",
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                error: error.payment_date || false,
                                                helperText: error.payment_date || '',
                                                onFocus: () => {
                                                    if (error.payment_date) {
                                                        onRestErrorKey("payment_date");
                                                    }
                                                }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => {
                                                    onFieldChange("date", null);
                                                    onRestErrorKey("date");
                                                }
                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid> : null}
                        {[2, 3, 5].includes(invoice?.status?.value) && ["Rental", "Reserve"].includes(invoiceType) && offsetDropdown?.length > 0 &&  (<Grid size={{ xs: 12 }}>
                            <Grid size={{ md: offsetDropdownDetail?.invoice_items?.length > 0 ? 4 : 6, xs: 12 }}>
                                <Typography variant="subtitle2">Link Credit Note for Offset</Typography>
                                <Autocomplete
                                    options={offsetDropdown}
                                    getOptionLabel={(option) => option.invoice_number || ""}
                                    isOptionEqualToValue={(option, value) => option.slug === value.slug}
                                    value={selectedOffset}
                                    onFocus={() => onRestErrorKey("offset_invoice_slugs")}
                                    onChange={(e, newValue) => {
                                        setSelectedOffset(newValue);
                                        setInvoice(prev => ({
                                            ...prev,
                                            offset_invoice_slugs: newValue ? [newValue.slug] : []
                                        }));
                                        setIsFullCnOffset(false);
                                        if (!newValue) {
                                            setOffsetDropdownDetail([]);
                                            setOffsetTableErrors({});
                                            return;
                                        }
                                        getOffsetDropdownDetail(newValue?.slug);
                                    }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                maxHeight: 100,
                                                overflowY: 'auto',
                                            },
                                        },
                                        popper: {
                                            placement: 'bottom',
                                        },
                                    }}
                                    renderInput={(params) => (<TextField {...params} label="Offset" margin='normal' size='small' helperText={error.offset_invoice_slugs}
                                        slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{dropDownloader?.offsetDropdownList ? (<CircularProgress color="inherit" size={20} />) : null} {params?.InputProps?.endAdornment} </>) } }}
                                        sx={{ marginTop: "10px" }} />)}
                                />
                            </Grid>
                            {showCnOffsetRadios && selectedOffset ? (
                                <Grid size={{ xs: 12 }}>
                                    <RadioGroup
                                        row
                                        value={isFullCnOffset ? 'apply_full_cn_offset' : 'apply_manually_cn_offset'}
                                        onChange={handleCnOffsetModeChange}
                                    >
                                        <FormControlLabel value="apply_manually_cn_offset" control={<Radio size="small" />} label="Apply Manually CN Offset" />
                                        <FormControlLabel value="apply_full_cn_offset" control={<Radio size="small" />} label="Apply Full CN Offset" />
                                    </RadioGroup>
                                </Grid>
                            ) : null}
                        </Grid>)}
                        {invoice?.status?.value === 4 ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <TextField
                                    id="status_change_reason"
                                    label="Reason"
                                    variant="outlined"
                                    size='small'
                                    value={invoice?.reason || ''}
                                    onChange={(e) => onFieldChange('reason', e.target.value.trimStart())}
                                    required
                                    margin='normal'
                                    fullWidth
                                    multiline
                                    rows={3}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                    error={error?.reason}
                                    helperText={error?.reason || ''}
                                    onFocus={() => onRestErrorKey("reason")}
                                />
                            </Grid> : null}
                        {invoice?.status?.value === 5 && !offsetDropdownDetail?.invoice_items?.length > 0 ? (
                            <Grid size={{ xs: 12 }}>
                                <Grid container spacing={0}>
                                    <Grid size={{ xs: Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0 ? 6 : 12 }}>
                                        <TableContainer className="lease-detail-table" component={Paper}>
                                            <Table className="mui-table-format" >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Item</TableCell>
                                                        {!titanAviationIntance && <TableCell>Description</TableCell>}
                                                        <TableCell sx={{ minWidth: '135px' }} >Original Amount</TableCell>
                                                        <TableCell sx={{ minWidth: '135px' }}>Remaining Amount</TableCell>
                                                        <TableCell sx={{ minWidth: '160px' }}>Paid Amount*</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {invoice?.invoice_items?.map((item, index) =>
                                                        <TableRow key={index}>
                                                            <TableCell>{item?.invoice_item_name}</TableCell>
                                                            {!titanAviationIntance && <TableCell>{item?.description}</TableCell>}
                                                            <TableCell><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{Math.floor(item?.item_total_amount * 100) / 100}</NumericLabel></TableCell>
                                                            <TableCell><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{Math.floor(item?.remaining_amount * 100) / 100}</NumericLabel></TableCell>
                                                            <TableCell sx={{ textAlign: "right", width: "160px" }}>
                                                                <TextField
                                                                    id="paid_amount"
                                                                    value={item?.remaining_amount === 0 && item?.item_total_amount === 0 ? numberWithCommas(0) : item?.remaining_amount === 0 ? "Paid" : (item?.paid_amount ?? 0)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    margin="none"
                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                    onChange={(e) => {
                                                                        const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                                                        if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                                            if (parseFloat(cleanValue) > (parseFloat(item?.remaining_amount) || 0)) return;
                                                                            onFieldChange('paid_amount', cleanValue, item, index);
                                                                            onRestErrorKey('all_paid_zero');
                                                                        } else { e.preventDefault(); }
                                                                    }}
                                                                    fullWidth
                                                                    disabled={item?.remaining_amount === 0}
                                                                    error={tableErrors?.[index]?.paid_amount}
                                                                    helperText={tableErrors?.[index]?.paid_amount}
                                                                    onFocus={() => resetTableError(index, "paid_amount")}
                                                                />
                                                            </TableCell>
                                                        </TableRow>)}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {invoice?.invoice_items?.length > 0 ?
                                            <Stack style={{ justifyContent: "flex-end", alignItems: "end", padding: "13px 0px 0px 0px", lineHeight: "8px" }} spacing={2}>
                                                <span style={{ color: '#090808af' }}>Total Remaining Amount Payable: <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code ? invoice?.currency?.code : invoice?.currency, commafy: true, precision: 2, justification: 'L' }}>{RemainingAmount || "0.00"}</NumericLabel></span>
                                                {invoice?.tax_details?.filter(i => i?.is_applied_on_total === true)?.map(i => <p className="invoice-balance-summary">{i?.name} ({getSignedTaxPercentage(i)}%): <span><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, commafy: true, precision: 2, justification: 'L' }}>{getSignedTaxAmount(i) || "0.00"}</NumericLabel></span></p>)}
                                                {invoice?.tax_details?.some(i => i?.is_applied_on_total === true) && <span style={{ color: '#090808af' }}>Total Amount: <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code ? invoice?.currency?.code : invoice?.currency, commafy: true, precision: 2, justification: 'L' }}>{invoice?.balance_amount || "0.00"}</NumericLabel></span>}
                                                <span style={{ color: '#00000073' }}>Total Paid Amount: <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code ? invoice?.currency?.code : invoice?.currency, commafy: true, precision: 2, justification: 'L' }}>{Amount || "0.00"}</NumericLabel></span>
                                            </Stack> : null}
                                </Grid>
                            </Grid>
                        </Grid>
                        ) : null}

                        {[3].includes(invoice?.status?.value) && ["Rental", "Reserve"].includes(invoiceType) && !offsetDropdownDetail?.invoice_items?.length > 0 && (<Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{invoice?.invoice_number} : Excess Amount</Typography>
                            <TableContainer className="lease-detail-table" component={Paper}>
                                <Table className="mui-table-format">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ minWidth: '145px' }} >Item</TableCell>
                                            <TableCell>Asset</TableCell>
                                            {invoice?.invoice_type?.label === "Reserve" ? <TableCell>Event</TableCell> : null}
                                            <TableCell sx={{ minWidth: '140px' }} >Amount</TableCell>
                                            <TableCell sx={{ textAlign: "center" }}>Excess Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice?.invoice_items?.map((item, index) =>
                                            <TableRow key={index}>
                                                <TableCell>{item?.invoice_item_name ? item?.invoice_item_name : '--'}</TableCell>
                                                <TableCell>{item?.asset ? item?.asset?.name : '--'}</TableCell>
                                                {invoice?.invoice_type?.label === "Reserve" ? <TableCell>{item?.events ? item?.events?.name : '--'}</TableCell> : null}
                                                <TableCell><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{item?.item_total_amount}</NumericLabel></TableCell>
                                                <TableCell sx={{ textAlign: "center" }}>
                                                    <TextField
                                                        id="amount"
                                                        value={item?.remaining_amount === 0 && item?.amount !== '' && item?.amount !== null && item?.amount !== undefined ? numberWithCommas(item?.amount) : (item?.amount ?? '')}
                                                        variant="outlined"
                                                        size="small"
                                                        style={{ width: "200px" }}
                                                        margin="none"
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                        onChange={(e) => {
                                                                    const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                                                    if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                                        onFieldChange('amount', cleanValue, item, index);
                                                                    } else { e.preventDefault(); }
                                                                }}
                                                        fullWidth
                                                        // disabled={item?.remaining_amount === 0}
                                                        error={tableErrors?.[index]?.amount}
                                                        helperText={tableErrors?.[index]?.amount}
                                                        onFocus={() => resetTableError(index, "amount")}
                                                    />
                                                </TableCell>
                                            </TableRow>)}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>)}

                        {/* Excess Amount (left) & Offset Detail (right) - single merged table */}
                        {[2, 3, 5].includes(invoice?.status?.value) && Array.isArray(offsetDropdownDetail?.invoice_items) && offsetDropdownDetail?.invoice_items?.length > 0 && (
                            <Grid size={{ xs: 12 }}>
                                <TableContainer className="lease-detail-table" component={Paper}>
                                    <Table className="mui-table-format">
                                        <TableHead>
                                            {/* Subtitle row */}
                                            <TableRow>
                                                <TableCell
                                                    colSpan={invoice?.invoice_type?.label === "Reserve" ? 4 : 3}
                                                    sx={{ fontWeight: 600, backgroundColor: '#f0f0f0', borderRight: '2px solid rgba(224,224,224,1)' }}
                                                >
                                                    Parent Invoice Detail
                                                </TableCell>
                                                <TableCell
                                                    colSpan={4}
                                                    sx={{ fontWeight: 600, backgroundColor: '#f0f0f0', borderRight: '2px solid rgba(224,224,224,1)' }}
                                                >
                                                    {offsetDropdownDetail?.invoice_number} : Offset Invoice Detail
                                                </TableCell>
                                                <TableCell
                                                    colSpan={invoice?.status?.value === 3 ? 3 : 2}
                                                    sx={{ backgroundColor: '#f0f0f0' }}
                                                />
                                            </TableRow>
                                            {/* Column headers row */}
                                            <TableRow>
                                                <TableCell>Asset / Item</TableCell>
                                                {invoice?.invoice_type?.label === "Reserve" ? <TableCell>Event</TableCell> : null}
                                                <TableCell sx={{ minWidth: '110px' }}>Invoice Amount</TableCell>
                                                <TableCell sx={{ minWidth: '110px', borderRight: '2px solid rgba(224,224,224,1)' }}>Remaining Amount</TableCell>
                                                <TableCell>Asset / Item</TableCell>
                                                <TableCell sx={{ minWidth: '100px' }}>Amount</TableCell>
                                                <TableCell sx={{ minWidth: '110px' }}>Remaining Amount</TableCell>
                                                <TableCell sx={{ minWidth: '100px' ,borderRight: '2px solid rgba(224,224,224,1)'}}>Offset Amount*</TableCell>
                                                <TableCell sx={{ minWidth: '100px' }}>Balance Amount</TableCell>
                                                {invoice?.status?.value === 5 ?<TableCell sx={{ minWidth: '100px' }}>Paid</TableCell> : null}
                                                {invoice?.status?.value === 3 ? <TableCell sx={{ minWidth: '100px' }}>Excess Amount</TableCell> : null}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {offsetMatchedRows.map((row, rowIdx) => {
                                                const { leftItem, rightItem, rightIndex } = row;
                                                const isUnmatchedOffsetRow = isFullCnOffset && !leftItem && rightItem;
                                                const unmatchedRowSx = isUnmatchedOffsetRow ? { backgroundColor: '#fda5b2f8 !important' } : {};
                                                return (
                                                    <TableRow key={rowIdx} sx={{ height: '53px' }}>
                                                        {/* Left: Excess Amount columns */}
                                                        <TableCell>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{leftItem?.asset?.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>{leftItem?.invoice_item_name || '--'}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        {invoice?.invoice_type?.label === "Reserve" ? <TableCell>{leftItem?.events?.name || '--'}</TableCell> : null}
                                                        <TableCell>
                                                            {leftItem ? <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{leftItem?.item_total_amount}</NumericLabel> : '--'}
                                                        </TableCell>
                                                        <TableCell sx={{ borderRight: '2px solid rgba(224,224,224,1)' }}>
                                                            {leftItem ? <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{leftItem?.remaining_amount}</NumericLabel> : '--'}
                                                        </TableCell>
                                                        {/* Right: Offset Detail columns */}
                                                        <TableCell sx={unmatchedRowSx}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{rightItem?.asset?.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>{rightItem?.invoice_item_name || '--'}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={unmatchedRowSx}>
                                                            {rightItem ? <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{rightItem?.item_total_amount}</NumericLabel> : '--'}
                                                        </TableCell>
                                                        <TableCell sx={unmatchedRowSx}>
                                                            {rightItem ? <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{rightItem?.remaining_amount}</NumericLabel> : '--'}
                                                        </TableCell>
                                                        <TableCell sx={{ borderRight: '2px solid rgba(224,224,224,1)', ...unmatchedRowSx }}>
                                                            {!rightItem || !leftItem || parseFloat(rightItem?.remaining_amount) === 0 ? 'N/A' : <TextField
                                                                id="offset_amount"
                                                                value={parseFloat(leftItem?.remaining_amount || 0) === 0 ? numberWithCommas(0) : (invoice?.status?.value === 2 ? numberWithCommas(rightItem?.offset_amount ?? 0) : (rightItem?.offset_amount ?? 0))}
                                                                variant="outlined"
                                                                size="small"
                                                                style={{ width: "100px" }}
                                                                margin="none"
                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                onChange={(e) => {
                                                                    const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                                                    if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                                        if (parseFloat(cleanValue) > parseFloat(rightItem?.remaining_amount || 0)) return;
                                                                        onOffsetItemChange('offset_amount', cleanValue, rightIndex, { paid_amount: 0 });
                                                                    } else { e.preventDefault(); }
                                                                }}
                                                                fullWidth
                                                                disabled={invoice?.status?.value === 2 || parseFloat(leftItem?.remaining_amount) === 0 || isFullCnOffset}
                                                                error={offsetTableErrors?.[rightIndex]?.offset_amount}
                                                                helperText={offsetTableErrors?.[rightIndex]?.offset_amount}
                                                                onFocus={() => resetOffsetTableError(rightIndex, "offset_amount")}
                                                            />}
                                                        </TableCell>
                                                        <TableCell>
                                                            {leftItem ? (() => { const bal = rightItem ? offsetBalanceByIndex[rightIndex] : (parseFloat(leftItem?.remaining_amount) || 0); return <span style={{ color: bal < 0 ? 'red' : 'inherit' }}><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{bal}</NumericLabel></span>; })() : '--'}
                                                        </TableCell>
                                                        {invoice?.status?.value === 5 ? <TableCell>
                                                            {!leftItem && rightItem ? 'N/A' : <TextField
                                                                id="paid_amount"
                                                                value={parseFloat(leftItem?.remaining_amount || 0) === 0 ? "Paid" : (invoice?.status?.value === 2 ? numberWithCommas(rightItem ? (rightItem?.paid_amount ?? 0) : (leftItem?.paid_amount ?? 0)) : (rightItem ? (rightItem?.paid_amount ?? 0) : (leftItem?.paid_amount ?? 0)))}
                                                                variant="outlined"
                                                                size="small"
                                                                style={{ width: "100px" }}
                                                                margin="none"
                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                onChange={(e) => {
                                                                    const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                                                    if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                                        const balanceAmount = (parseFloat(leftItem?.remaining_amount) || 0) - (parseFloat(rightItem?.offset_amount) || 0);
                                                                        if (parseFloat(cleanValue) > balanceAmount) return;
                                                                        if (rightItem) {
                                                                            onOffsetItemChange('paid_amount', cleanValue, rightIndex);
                                                                        } else {
                                                                            onFieldChange('paid_amount', cleanValue, leftItem, rowIdx);
                                                                        }
                                                                        onRestErrorKey('all_paid_zero');
                                                                    } else { e.preventDefault(); }
                                                                }}
                                                                fullWidth
                                                                disabled={invoice?.status?.value === 2 || parseFloat(leftItem?.remaining_amount || 0) === 0}
                                                                error={rightItem ? offsetTableErrors?.[rightIndex]?.paid_amount : tableErrors?.[rowIdx]?.paid_amount}
                                                                helperText={rightItem ? offsetTableErrors?.[rightIndex]?.paid_amount : tableErrors?.[rowIdx]?.paid_amount}
                                                                onFocus={() => rightItem ? resetOffsetTableError(rightIndex, "paid_amount") : resetTableError(rowIdx, "paid_amount")}
                                                            />}
                                                        </TableCell> : null }
                                                        {invoice?.status?.value === 3 ? (
                                                            <TableCell>
                                                                {!leftItem && rightItem ? 'N/A' : <TextField
                                                                    id="amount"
                                                                    value={(() => {
                                                                        const isDisabled = invoice?.status?.value === 2 || parseFloat(leftItem?.remaining_amount || 0) === 0;
                                                                        const rawValue = rightItem ? (rightItem?.amount ?? '') : (leftItem?.amount ?? '');
                                                                        return isDisabled && rawValue !== '' && rawValue !== null && rawValue !== undefined ? numberWithCommas(rawValue) : rawValue;
                                                                    })()}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    style={{ width: "100px" }}
                                                                    margin="none"
                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                    onChange={(e) => {
                                                                        const cleanValue = e.target.value.replace(/[^0-9.]/g, '');
                                                                        if (regexConstants.decimalnodotstart.test(cleanValue)) {
                                                                            if (rightItem) {
                                                                                onOffsetItemChange('amount', cleanValue, rightIndex);
                                                                            } else {
                                                                                onFieldChange('amount', cleanValue, leftItem, rowIdx);
                                                                            }
                                                                        } else { e.preventDefault(); }
                                                                    }}
                                                                    fullWidth
                                                                    error={rightItem ? offsetTableErrors?.[rightIndex]?.amount : tableErrors?.[rowIdx]?.amount}
                                                                    helperText={rightItem ? offsetTableErrors?.[rightIndex]?.amount : tableErrors?.[rowIdx]?.amount}
                                                                    onFocus={() => rightItem ? resetOffsetTableError(rightIndex, "amount") : resetTableError(rowIdx, "amount")}
                                                                />}
                                                            </TableCell>
                                                        ) : null}
                                                    </TableRow>
                                                );
                                            })}
                                            {/* Total row */}
                                            <TableRow className="mui-table-last-row">
                                                <TableCell>Total</TableCell>
                                                {invoice?.invoice_type?.label === "Reserve" ? <TableCell /> : null}
                                                <TableCell>
                                                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                        {invoice?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.item_total_amount) || 0), 0)}
                                                    </NumericLabel>
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '2px solid rgba(224,224,224,1)' }}>
                                                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                        {invoice?.balance_amount}
                                                    </NumericLabel>
                                                </TableCell>
                                                <TableCell />
                                                <TableCell>
                                                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                        {offsetDropdownDetail?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.item_total_amount) || 0), 0)}
                                                    </NumericLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                        {offsetDropdownDetail?.balance_amount}
                                                    </NumericLabel>
                                                </TableCell>
                                                <TableCell sx={{ borderRight: '2px solid rgba(224,224,224,1)' }}>
                                                    {(() => {
                                                        const totalOffsetAmount = offsetDropdownDetail?.invoice_items?.reduce((sum, item) => sum + (parseFloat(item?.offset_amount) || 0), 0) || 0;
                                                        const offsetBalanceAmount = parseFloat(offsetDropdownDetail?.balance_amount) || 0;
                                                        return (
                                                            <span style={{ color: totalOffsetAmount > offsetBalanceAmount ? 'red' : 'inherit' }}>
                                                                <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                                    {totalOffsetAmount}
                                                                </NumericLabel>
                                                            </span>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                   {(() => {
                                                       const totalBalanceAmount = offsetMatchedRows.reduce((sum, row) => row.leftItem ? sum + ((parseFloat(row.leftItem?.remaining_amount) || 0) - (parseFloat(row.rightItem?.offset_amount) || 0)) : sum, 0);
                                                       return <span style={{ color: totalBalanceAmount < 0 ? 'red' : 'inherit' }}><NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{totalBalanceAmount}</NumericLabel></span>;
                                                   })()}
                                                </TableCell>
                                                {invoice?.status?.value === 5 ? <TableCell>
                                                    {(() => {
                                                        const totaloffsetMatchedRows = offsetMatchedRows.reduce((sum, row) => sum + (parseFloat((row.rightItem ? row.rightItem?.paid_amount : row.leftItem?.paid_amount) || 0) || 0), 0)
                                                        const paidBalanceAmount = parseFloat(invoice?.balance_amount) || 0;
                                                        return (
                                                            <span style={{ color: totaloffsetMatchedRows > paidBalanceAmount ? 'red' : 'inherit' }}>
                                                                <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                                    {totaloffsetMatchedRows}
                                                                </NumericLabel>
                                                            </span>
                                                        );
                                                    })()}
                                                </TableCell> : null }
                                                {invoice?.status?.value === 3 ? (
                                                    <TableCell>
                                                        <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: invoice?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>
                                                            {offsetMatchedRows.reduce((sum, row) => sum + (parseFloat((row.rightItem ? row.rightItem?.amount : row.leftItem?.amount) || 0) || 0), 0)}
                                                        </NumericLabel>
                                                    </TableCell>
                                                ) : null}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ paddingRight: "16px" }}>
                    <Button className='cancel-btn' variant='outlined' size='small' disabled={isLoading} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }} > Cancel </Button>
                    <Button variant='contained' className='btn-primary' disabled={isLoading || !isDataChanged()} size='small' onClick={() => { onChangeInvoiceStatus(); handleItemClicked('Save Button') }}>
                        {isLoading ? <CircularProgress color='inherit' size={25} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading || dropDownloader?.offsetDropdownList || isFetchingPartialItems ? <PageLoader /> : null}
        </>
    )
}
export default ChangeInvoiceStatus;